import { all, takeLatest, put } from 'redux-saga/effects';

import { requestAndSet, sendToHelium } from 'utils/helpers';

import { redFlagActions, RedFlagState } from '../slices/redFlagSlice';

const loading = (key: keyof RedFlagState) => (v: boolean) =>
  redFlagActions.setLoading({ key, value: v });

function* fetchRedFlagsSaga(
  action: ReturnType<typeof redFlagActions.fetchRedFlags>,
): Generator {
  const { orgId, isMatchingRedFlags, ...params } = action.payload;
  yield requestAndSet('listAllRedFlag', [orgId, null], {
    queryStringParams: params,
    createSetterAction: isMatchingRedFlags
      ? redFlagActions.setMatchingRedFlags
      : redFlagActions.setRedFlags,
    loadingAction: loading('fetchRedFlagsLoading'),
  });
}

function* describeRedFlagSaga(
  action: ReturnType<typeof redFlagActions.describeRedFlag>,
): Generator {
  const { orgId, redFlagId } = action.payload;
  yield requestAndSet('describeRedFlag', [orgId, redFlagId], {
    createSetterAction: redFlagActions.setSelectedRedFlag,
    loadingAction: loading('describeRedFlagLoading'),
  });
}

function* createRedFlagSaga(
  action: ReturnType<typeof redFlagActions.createRedFlag>,
): Generator {
  const { orgId, redFlag } = action.payload;
  const result = yield sendToHelium('newRedFlag', [orgId], redFlag, {
    loadingAction: loading('createRedFlagLoading'),
  });
  if (result?.statusCode === 201 || result?.statusCode === 200) {
    yield put(redFlagActions.fetchRedFlags({ orgId }));
  }
}

function* updateRedFlagSaga(
  action: ReturnType<typeof redFlagActions.updateRedFlag>,
): Generator {
  const { orgId, redFlagId, redFlag } = action.payload;
  const result = yield sendToHelium(
    'updateRedFlag',
    [orgId, redFlagId],
    redFlag,
    {
      loadingAction: loading('updateRedFlagLoading'),
    },
  );
  if (result?.statusCode === 204 || result?.statusCode === 200) {
    yield put(redFlagActions.fetchRedFlags({ orgId }));
    yield put(redFlagActions.fetchRedFlagCount({ orgId }));
  }
}

function* fetchRedFlagDataSaga(
  action: ReturnType<typeof redFlagActions.fetchRedFlagData>,
): Generator {
  yield requestAndSet(
    'getRedFlagData',
    [action.payload.orgId, action.payload.redFlagId],
    {
      createSetterAction: redFlagActions.setRedFlagData,
      loadingAction: loading('fetchRedFlagDataLoading'),
    },
  );
}

function* fetchRedFlagLogsSaga(
  action: ReturnType<typeof redFlagActions.fetchRedFlagLogs>,
): Generator {
  yield requestAndSet(
    'getRedFlagLog',
    [action.payload.orgId, action.payload.redFlagId],
    {
      createSetterAction: redFlagActions.setRedFlagLogs,
      loadingAction: loading('fetchRedFlagLogsLoading'),
    },
  );
}

function* createRedFlagLogSaga(
  action: ReturnType<typeof redFlagActions.createRedFlagLog>,
): Generator {
  const { orgId, redFlagId, redFlagLog } = action.payload;
  const result = yield sendToHelium(
    'newRedFlagLog',
    [orgId, redFlagId],
    redFlagLog,
    {
      loadingAction: loading('createRedFlagLogLoading'),
    },
  );
  if (result) yield put(redFlagActions.fetchRedFlagLogs({ orgId, redFlagId }));
}

function* fetchRedFlagCountSaga(
  action: ReturnType<typeof redFlagActions.fetchRedFlagCount>,
): Generator {
  yield requestAndSet('getRedFlagCount', [action.payload.orgId], {
    createSetterAction: redFlagActions.setRedFlagCount,
    loadingAction: loading('fetchRedFlagCountLoading'),
  });
}

export default function* redFlagSaga() {
  yield all([
    takeLatest(redFlagActions.fetchRedFlags.type, fetchRedFlagsSaga),
    takeLatest(redFlagActions.describeRedFlag.type, describeRedFlagSaga),
    takeLatest(redFlagActions.createRedFlag.type, createRedFlagSaga),
    takeLatest(redFlagActions.updateRedFlag.type, updateRedFlagSaga),
    takeLatest(redFlagActions.fetchRedFlagData.type, fetchRedFlagDataSaga),
    takeLatest(redFlagActions.fetchRedFlagLogs.type, fetchRedFlagLogsSaga),
    takeLatest(redFlagActions.createRedFlagLog.type, createRedFlagLogSaga),
    takeLatest(redFlagActions.fetchRedFlagCount.type, fetchRedFlagCountSaga),
  ]);
}
